import { isCategory, isSubCategory } from '@eventbrite/categories';
import { gettext } from '@eventbrite/i18n';
import { setNavigatorLastLocation } from '@eventbrite/personalization';
import {
    getLocationData,
    locationDuck,
    setLocationData,
} from '@eventbrite/redux-destination';
import * as analytics from '@eventbrite/site-analytics';
import {
    deepKeysToCamel,
    keysCamelToSnake,
} from '@eventbrite/transformation-utils';
import { $FixMe } from '@eventbrite/ts-utils';
import compact from 'lodash/compact';
import filter from 'lodash/filter';
import includes from 'lodash/includes';
import omit from 'lodash/omit';
import uniq from 'lodash/uniq';
import { StringFlag } from 'meow';
import {
    ALL_EVENTS_TYPE,
    CURRENT_FUTURE,
    CUSTOM_DATE,
    EVENTS_TYPE,
    FORMAT_TAG_TYPE,
} from '../../constants';
import {
    GA_LOCATION_CHANGE_ACTION,
    GA_REDO_FROM_MAP,
    GA_SEARCH_CATEGORY,
    GA_VIEW_EVENT_MAP_VIEW,
} from '../../constants/analytics';
import { CLEAR_PRICE_FILTER } from '../../constants/filters';
import {
    bboxShape,
    EventSearch,
    PointRadius,
    SuggestionsState,
} from '../../types/index';
import { getPlaceFromRequest as getPlaceFromRequestApi } from '../api/location';
import {
    searchAutocompleteSuggestions as searchAutocompleteSuggestionsApi,
    searchEventSuggestions as searchEventSuggestionsApi,
} from '../api/search';
import { getFilterEventTrackingData } from '../utils/analytics';
import {
    parseSuggestions,
    transformSearchIntoSuggestions,
} from '../utils/suggestions';
import { fetchThingsToDoShelfData } from './thingsToDoShelf';
import { showErrorNotification } from './ui';

const { trackEventFromState } = analytics;
const {
    actions: { updateLocation },
} = locationDuck;

export const UPDATE_SEARCH_CRITERIA = 'UPDATE_SEARCH_CRITERIA';
export const CLEAN_TRACKING_DATA = 'CLEAN_TRACKING_DATA';

export const updateSearchCriteria = (
    searchQuery: Omit<EventSearch, 'bbox'>,
    gaTrackingData: $FixMe[] = [],
) => ({
    type: UPDATE_SEARCH_CRITERIA,
    payload: {
        eventSearch: searchQuery,
        gaTrackingData,
    },
});

const SEARCH_RADIUS_DEFAULTS = {
    radius: '150km',
    offset: '15km',
    scale: '15km',
};

export const UPDATE_SEARCH_SUGGESTIONS = 'UPDATE_SEARCH_SUGGESTIONS';
export const SUGGESTIONS_LOADING = 'SUGGESTIONS_LOADING';
const updateSearchAutocompleteSuggestions = (
    suggestions?: SuggestionsState,
) => ({
    type: UPDATE_SEARCH_SUGGESTIONS,
    payload: suggestions,
});

export const searchAutocompleteSuggestions =
    (query?: string) => (dispatch: Function, getState?: Function) => {
        if (!query) {
            return dispatch(updateSearchAutocompleteSuggestions({}));
        }

        const {
            location: { placeId, isOnline },
        } = getState?.();

        dispatch({ type: SUGGESTIONS_LOADING });

        return searchAutocompleteSuggestionsApi({
            placeId,
            onlineEventsOnly: isOnline,
            query,
        })
            .then((response: any) =>
                dispatch(
                    updateSearchAutocompleteSuggestions(
                        parseSuggestions(response),
                    ),
                ),
            )
            .catch(() => {
                dispatch(
                    showErrorNotification(
                        gettext(
                            'Error finding events. Please refresh and try again',
                        ),
                    ),
                );
            });
    };

export const searchEventSuggestions =
    (forceOnline = false) =>
    (dispatch: Function, getState: Function) => {
        const {
            location: { placeId, isOnline },
        } = getState();
        dispatch({ type: SUGGESTIONS_LOADING });
        return searchEventSuggestionsApi({
            placeId: forceOnline ? undefined : placeId,
            onlineEventsOnly: forceOnline || isOnline,
        })
            .then((response: any) =>
                dispatch(
                    updateSearchAutocompleteSuggestions(
                        parseSuggestions(
                            transformSearchIntoSuggestions(response),
                        ),
                    ),
                ),
            )
            .catch(() => {
                //do nothing since this happens behind the scenes to
                //pre-populate the event suggestion dropdown.
            });
    };

export const handleRemoveOnlineFilter =
    (history: object, _args: $FixMe) =>
    (dispatch: Function, getstate: Function) => {
        const { location } = getstate();

        if (location.fallBack) {
            dispatch(
                searchInformationForPlace({
                    ...location.fallBack,
                    history,
                }),
            );
        } else {
            // The user arrived to an `online` search or browse page directly.
            // So we make a best guess based on the available information:

            //1. Cookie with location data is stored
            //2. IP lookup to get a best guess
            const locationData = getLocationData();
            if (locationData && locationData.placeId) {
                dispatch(
                    searchInformationForPlace({
                        ...locationData,
                        isOnline: false,
                        history,
                    }),
                );
            } else {
                getPlaceFromRequestApi()
                    .then(({ place }: { place: string }) => {
                        dispatch(
                            searchInformationForPlace({
                                ...deepKeysToCamel(place),
                                isOnline: false,
                                history,
                            }),
                        );
                    })
                    .catch(() => {
                        showErrorNotification(
                            gettext(
                                'Unable to find your current location. Please select one',
                            ),
                        );
                    });
            }
        }
    };

interface searchInformationForPlaceProps {
    slug?: string;
    placeId?: string;
    latitude?: number;
    longitude?: number;
    placeType?: string;
    currentPlace?: string;
    currentPlaceParent?: string;
    isUsingCurrentLocation?: boolean;
    isOnline?: boolean;
    history?: object;
    userLatLng?: GeolocationCoordinates;
}

export const searchInformationForPlace =
    (
        {
            slug,
            placeId,
            latitude,
            longitude,
            placeType,
            currentPlace,
            currentPlaceParent,
            isUsingCurrentLocation,
            isOnline = false,
            userLatLng,
        }: searchInformationForPlaceProps,
        query?: string,
    ) =>
    (dispatch: Function, getState: Function) => {
        const {
            search: { eventSearch },
            location: oldLocation,
        } = getState();

        const label = `${oldLocation.currentPlace}--${currentPlace}`;

        const gaTrackingData = {
            label,
            category: GA_SEARCH_CATEGORY,
            action: GA_LOCATION_CHANGE_ACTION,
        };

        const locationData = {
            slug,
            placeId,
            latitude,
            longitude,
            placeType,
            currentPlace,
            currentPlaceParent,
            isUsingCurrentLocation,
            isOnline,
            userLatLng,
        };

        dispatch({
            type: 'SET_FALLBACK_LOCATION',
            payload: oldLocation,
        });

        dispatch(updateLocation(locationData));

        // See https://docs.google.com/document/d/1Vg-kyxM34gw71vw_5dLcfzFRhzM3scQ3FKtYUlWXLSA/edit?usp=sharing
        placeId && setLocationData(keysCamelToSnake(locationData));

        /* When the user selects "online" from the Recent suggestions the placeId is null and the slug is online,
        but the isOnline is false. Because of that we should change it to reflect the correct filter */
        if (!placeId && slug === 'online') {
            locationData.isOnline = true;
        }

        if (!locationData.isOnline && !!placeId) {
            setNavigatorLastLocation({
                currentPlace,
                currentPlaceParent,
                content: currentPlace,
                secondaryContent: currentPlaceParent,
                value: `recent-${placeId}`,
                latitude,
                longitude,
                placeType,
                placeId,
                slug,
                isOnline,
                isHistorySuggestion: true,
            });
        }

        dispatch(searchEventSuggestions());

        const isPointRadiusActive = !!Object.keys(eventSearch.pointRadius)
            .length;

        const searchQuery = {
            ...eventSearch,
            // Since we are searching by place we can remove the bbox
            bbox: '',
            q: !(!query && query !== '') ? query : eventSearch.q,
            page: 1,
            onlineEventsOnly: isOnline,
            places: isOnline ? [] : [placeId],
            pointRadius: isOnline
                ? {}
                : {
                      ...eventSearch.pointRadius,
                      ...(isPointRadiusActive ? { latitude, longitude } : {}),
                      ...(isUsingCurrentLocation && userLatLng
                          ? {
                                latitude: userLatLng.latitude,
                                longitude: userLatLng.longitude,
                            }
                          : {}),
                      ...(isUsingCurrentLocation &&
                      userLatLng &&
                      !isPointRadiusActive
                          ? {
                                ...SEARCH_RADIUS_DEFAULTS,
                            }
                          : {}),
                  },
        };

        dispatch(updateSearchCriteria(searchQuery, [gaTrackingData]));
        dispatch(fetchThingsToDoShelfData());
    };

export const cleanLongitude = (longitude: number) =>
    (((longitude % 360) + 540) % 360) - 180;

export const searchByBBox =
    (_history?: object, bbox?: bboxShape) =>
    (dispatch: Function, getState: Function) => {
        const state = getState();
        const {
            search: { eventSearch },
            location: { slug },
        } = state;

        // Create bounding box string of shape: '${left},${bottom},${right},${top}'
        const bboxParam = `${cleanLongitude(bbox?.sw?.lng || 0)},${
            bbox?.sw?.lat
        },${cleanLongitude(bbox?.ne?.lng || 0)},${bbox?.ne?.lat}`;

        const searchQuery: EventSearch = {
            ...eventSearch,
            pointRadius: {},
            onlineEventsOnly: false,
            bbox: bboxParam,
        };

        const gaTrackingData = {
            label: slug,
            category: GA_SEARCH_CATEGORY,
            action: GA_REDO_FROM_MAP,
        };

        dispatch(
            updateSearchCriteria(omit(searchQuery, ['places']), [
                gaTrackingData,
            ]),
        );
    };

// If a bounding box is available, use it.  If not, search by place
export const searchByBBoxOrPlace =
    (history: object, place: $FixMe & { bbox: bboxShape }, query?: string) =>
    (dispatch: Function) => {
        if ('bbox' in place) {
            dispatch(searchByBBox(history, place.bbox));
        } else {
            dispatch(searchInformationForPlace({ ...place, history }, query));
        }
    };

export const viewEventMapView =
    (eventId: string) => (_: any, getState: Function) => {
        const state = getState();

        trackEventFromState(state, {
            label: eventId,
            category: GA_SEARCH_CATEGORY,
            action: GA_VIEW_EVENT_MAP_VIEW,
        });
    };

const _isTagType = (tag: string, type: string) => tag.indexOf(type) > -1;

const _getUpdatedTags = (
    currentTags: string[],
    subcategories?: string | string[],
    category?: string,
    format?: string,
) => {
    //Return current tags if no tag or format passed
    if (
        subcategories === undefined &&
        category === undefined &&
        format === undefined
    ) {
        return currentTags;
    }

    let updatedTags = [...currentTags];
    let filteredTags: string[] = [];

    if ((category && isCategory(category)) || category === '') {
        //updated tags should equal the new array minus the previously selected category and subcategories
        filteredTags = updatedTags
            .filter((val) => !isCategory(val))
            .filter((val) => category === '' || !isSubCategory(val));
        updatedTags = [...filteredTags, category];
    }

    if (format || format === '') {
        updatedTags = [
            ...filter(updatedTags, (val) => !_isTagType(val, FORMAT_TAG_TYPE)),
            format,
        ];
    }

    if (subcategories) {
        const filteredSubcategories = (
            Array.isArray(subcategories) ? subcategories : [subcategories]
        ).filter(isSubCategory);
        // remove any subcategories
        filteredTags = updatedTags.filter((val) => !isSubCategory(val));

        // combine categories + formats with the subcategories
        updatedTags = uniq([...filteredTags, ...filteredSubcategories]).sort();
    }

    return compact(updatedTags);
};

/**
 *
 * @param currentTags The current list of tags.
 * @param filters An object indicating which tags to remove
 * @returns A filtered version of the current tags with any tags matching the filters removed
 */
const _getUpdatedTagsForRemoval = (
    currentTags: string[],
    { subcategories = false, category = false, format = false },
) => {
    // This approach assues there will only ever be at most one of each type
    // If that is to change than the logic here will need to be updated
    return compact(
        currentTags.map((currentTag: string) => {
            if (
                (category && isCategory(currentTag)) ||
                (format && _isTagType(currentTag, FORMAT_TAG_TYPE)) ||
                (subcategories && isSubCategory(currentTag))
            ) {
                return '';
            }

            return currentTag;
        }),
    );
};

const _handlePriceUpdate = (price: string, priceInState: string) => {
    let priceValue = priceInState;

    if (price) {
        priceValue = price === CLEAR_PRICE_FILTER ? '' : price;
    }

    return priceValue;
};

const _getUpdatedDates = (dates: string, datesInState: string) => {
    if (dates === CUSTOM_DATE) {
        return CURRENT_FUTURE;
    } else if (dates) {
        return [CURRENT_FUTURE, dates];
    }
    return datesInState;
};

const _getUpdatedDateRange = (
    dates: string,
    startDate?: string,
    endDate?: string,
    dateRangeInState?: StringFlag,
) => {
    if (startDate && endDate) {
        return { from: startDate, to: endDate };
    }
    if (dates) {
        return {};
    }
    return dateRangeInState;
};

const _getUpdatedArrayFilter = (filter: string, filterInState: string) => {
    if (filter) {
        if (typeof filter === 'string') {
            return [filter];
        } else {
            return filter;
        }
    }

    return filterInState;
};

const _getSpecialParams = (filterInState: string, filter?: string) => {
    if (!filter) {
        return filterInState;
    }

    return uniq([...filterInState, filter]);
};

const _getUpdatedPointRadius = (
    pointRadiusInState: PointRadius,
    distance?: string,
    location?: $FixMe,
) => {
    if (distance) {
        //If user is already using their current location,
        //the location will be embedded in pointRadiusInState
        //If not we must grab it from location in state.
        return {
            radius: distance,
            offset: undefined,
            scale: undefined,
            latitude: pointRadiusInState?.latitude || location.latitude,
            longitude: pointRadiusInState?.longitude || location.longitude,
        };
    }

    return pointRadiusInState;
};

interface handleApplyFilterProps {
    category?: string;
    subcategories?: string[];
    price?: string;
    dates?: string;
    q?: string;
    format?: string;
    languages?: any;
    currencies?: string;
    special?: string;
    hash?: string;
    distance?: string;
}

export const handleApplyFilter =
    (
        // TODO: This param should be removed
        _history: object,
        {
            category,
            subcategories,
            price,
            dates,
            q,
            format,
            languages,
            currencies,
            special,
            hash,
            distance,
        }: handleApplyFilterProps,
        { startDate, endDate }: { startDate?: string; endDate?: string } = {},
    ) =>
    (dispatch: Function, getState: Function) => {
        const {
            search: { eventSearch },
            location,
        } = getState();
        const gaTrackingData = getFilterEventTrackingData(
            {
                category,
                subcategories,
                price,
                dates,
                q,
                format,
                languages,
                currencies,
                special,
            },
            { startDate, endDate },
        );

        const updateSpecialParams = _getSpecialParams(
            eventSearch.special,
            special,
        );
        const updatedTags = _getUpdatedTags(
            eventSearch.tags,
            subcategories,
            category,
            format,
        );
        const updatedPrice = _handlePriceUpdate(price || '', eventSearch.price);
        const updatedDates = _getUpdatedDates(dates || '', eventSearch.dates);
        const updatedCurrencies = _getUpdatedArrayFilter(
            currencies || '',
            eventSearch.currencies,
        );
        const updatedLanguages = languages || eventSearch.languages;
        const updatedDateRange = _getUpdatedDateRange(
            dates || '',
            startDate,
            endDate,
            eventSearch.dateRange,
        );
        const updatedHash = hash || eventSearch.hash;
        const updatedPointRadius = _getUpdatedPointRadius(
            eventSearch.pointRadius,
            distance,
            location,
        );

        let searchQuery = {
            ...eventSearch,
            q: !(!q && q !== '') ? q : eventSearch.q,
            price: updatedPrice,
            tags: updatedTags,
            dates: updatedDates,
            dateRange: updatedDateRange,
            currencies: updatedCurrencies,
            languages: updatedLanguages,
            special: updateSpecialParams,
            hash: updatedHash,
            pointRadius: updatedPointRadius,
            bbox: distance ? '' : eventSearch.bbox,
            page: 1,
        };

        // when searching for 'events' or 'all events' filters should be resetted
        if (
            includes(
                [EVENTS_TYPE, ALL_EVENTS_TYPE],
                (searchQuery.q || '').toLowerCase(),
            )
        ) {
            searchQuery = {
                ...eventSearch,
                q: '',
                price: '',
                category: '',
                tags: [],
                dates: CURRENT_FUTURE,
                dateRange: '',
                page: 1,
                currencies: '',
                languages: '',
                pointRadius: {},
            };
        }

        dispatch(updateSearchCriteria(searchQuery, gaTrackingData));
    };

/**
 *
 * @param {*} history
 * @param {*} simpleQuery
 *
 * Takes in a preconfigured query. Resets the current search state to
 * null, and applies the passed in query.
 */
export const handleApplyFilterSimple =
    (
        // TODO: This param should be removed
        _history: object,
        simpleQuery: {
            subcategories?: string[];
            category?: string;
            format?: string;
            dates?: string;
            remainingQuery: any;
        },
    ) =>
    (dispatch: Function, getState: Function) => {
        const {
            search: { eventSearch },
        } = getState();

        const {
            subcategories = [],
            category = '',
            format = '',
            dates = '',
            ...remainingQuery
        } = simpleQuery;

        const updatedDates = dates ? [CURRENT_FUTURE, dates] : CURRENT_FUTURE;
        const updatedTags = _getUpdatedTags(
            [],
            subcategories,
            category,
            format,
        );

        const searchQuery = {
            ...eventSearch,
            q: '',
            price: '',
            dateRange: {},
            currencies: '',
            languages: '',
            page: 1,
            special: [],
            ...{ tags: updatedTags, dates: updatedDates, ...remainingQuery },
        };

        //This doesn't support custom dates. Will need additional work
        const gaTrackingData = getFilterEventTrackingData({ ...searchQuery });

        dispatch(updateSearchCriteria(searchQuery, gaTrackingData));
    };

interface handleRemoveFilterProps {
    price?: string;
    dates?: string[];
    category?: boolean;
    subcategories?: boolean;
    format?: boolean;
    currencies?: string;
    languages?: string;
    special?: string;
    organizationsOr?: string[];
    hash?: string;
    places?: string[];
    distance?: string;
}

const getClearDistanceFilter = (location: any, eventSearch: EventSearch) => {
    const pointRadius = eventSearch.pointRadius;

    //Distance filter will only be cleared if the user is enrolled
    //in the experiment. Given that, if they are sharing their location
    //with us that means the pointRadius filter is holding onto the
    //user's current location.
    const isUsingCurrentLocation = location.isUsingCurrentLocation;

    //If user is sharing their current location remove
    //the distance filter specific radius and reapply
    //the offset/scale to normalize ranking with a standard
    //search.
    if (isUsingCurrentLocation) {
        return {
            ...pointRadius,
            ...SEARCH_RADIUS_DEFAULTS,
        };
    }

    //Handle case where the previous "place" is missing due to
    //an unfound place or other reason, we still want the search
    //to work as expected. So if there is not placeId representation
    //in the location reducer and it is not a map based search
    //we will want to keep using the lat/lng but with a standardized distance
    if (!location.placeId && !eventSearch.bbox && !location.isOnline) {
        return {
            ...pointRadius,
            ...SEARCH_RADIUS_DEFAULTS,
        };
    }

    return {};
};

export const handleRemoveFilter =
    (
        // TODO: This param should be removed
        _history: object,
        {
            category,
            subcategories,
            price,
            dates,
            format,
            currencies,
            languages,
            special,
            organizationsOr,
            hash,
            distance,
        }: handleRemoveFilterProps,
    ) =>
    (dispatch: Function, getState: Function) => {
        const {
            search: { eventSearch },
            location,
        } = getState();

        const updatedTags = _getUpdatedTagsForRemoval(eventSearch.tags, {
            category,
            subcategories,
            format,
        });

        const searchQuery = {
            ...eventSearch,
            tags: updatedTags,
            price: price ? '' : eventSearch.price,
            dates: dates ? CURRENT_FUTURE : eventSearch.dates,
            dateRange: dates ? {} : eventSearch.dateRange,
            currencies: currencies ? '' : eventSearch.currencies,
            languages: languages ? '' : eventSearch.languages,
            organizationsOr: organizationsOr ? [] : eventSearch.organizationsOr,
            hash: hash ? '' : eventSearch.hash,
            special: special ? [] : eventSearch.special,
            page: 1,
            pointRadius: distance
                ? getClearDistanceFilter(location, eventSearch)
                : eventSearch.pointRadius,
        };

        dispatch(updateSearchCriteria(searchQuery));
    };

export const handleRemoveAllFilters =
    // TODO: history and args are not needed
    (_history: object, _args?: any) => (dispatch: Function) => {
        // Since we want to remove only the filters in the filter panel,
        // we don't want to spread ...eventSearch since that would reset
        // the q as well
        const searchQuery = {
            tags: [],
            price: undefined,
            dates: CURRENT_FUTURE,
            dateRange: {},
            currencies: '',
            languages: '',
            page: 1,
            pointRadius: {},
        };

        dispatch(updateSearchCriteria(searchQuery));
    };
